/*
 * C:\Sovereign\Projects\Memorials\memorial-intake\Step5Review.tsx
 *
 * STEP 5 — Review & Submit
 * Design: Quiet Reverence
 *
 * Flow: Review → Upload to R2 → POST to Worker /api/intake → Confirmation
 *
 * ZERO Stripe. ZERO payment redirect. ZERO /api/stripe/create-checkout.
 * Files are uploaded directly to sovereign-inbound R2 via pre-signed URLs.
 * The Worker writes the order row to D1 and fires the sovereign webhook.
 */

import { IntakeFormState, TIER_INFO, TONE_INFO } from "./intake";
import { Camera, Film, Loader2, Mic, Music, Send } from "lucide-react";

interface Props {
  state: IntakeFormState;
  additionalNotes: string;
  onNotesChange: (v: string) => void;
  onSubmit: () => void;
  submitting: boolean;
  uploadProgress: number;
}

export default function Step5Review({
  state,
  additionalNotes,
  onNotesChange,
  onSubmit,
  submitting,
  uploadProgress,
}: Props) {
  const { deceased, photos, videos, audio } = state;
  const voicemails = audio.filter((a) => a.type === "voicemail");
  const music      = audio.filter((a) => a.type === "music");
  const fullName   = `${deceased.firstName} ${deceased.lastName}`.trim() || "Your Loved One";

  return (
    <div className="step-enter space-y-6">
      {/* Summary Header */}
      <div className="p-5 rounded-xl bg-primary/5 border border-primary/20 space-y-1">
        <h3 className="font-['Playfair_Display'] font-700 text-xl text-foreground">{fullName}</h3>
        {deceased.dateOfBirth && deceased.dateOfPassing && (
          <p className="text-sm text-muted-foreground font-['Playfair_Display'] italic">
            {deceased.dateOfBirth} — {deceased.dateOfPassing}
          </p>
        )}
        <div className="flex gap-3 mt-2 flex-wrap">
          <span className="text-xs px-2.5 py-1 rounded-full bg-primary/10 text-primary font-medium">
            {TIER_INFO[deceased.tier].label} · {TIER_INFO[deceased.tier].price}
          </span>
          <span className="text-xs px-2.5 py-1 rounded-full bg-secondary text-secondary-foreground font-medium">
            {TONE_INFO[deceased.tone].label}
          </span>
        </div>
      </div>

      {/* Asset Summary */}
      <div className="grid grid-cols-2 gap-3">
        <SummaryCard icon={<Camera className="w-5 h-5 text-primary" />} count={photos.length}     label="Photos"         />
        <SummaryCard icon={<Film   className="w-5 h-5 text-primary" />} count={videos.length}     label="Video Clips"    />
        <SummaryCard icon={<Mic    className="w-5 h-5 text-primary" />} count={voicemails.length} label="Voice Tributes" />
        <SummaryCard icon={<Music  className="w-5 h-5 text-primary" />} count={music.length}      label="Music Tracks"   />
      </div>

      {/* Contact Summary */}
      <div className="p-4 rounded-xl border border-border bg-card space-y-2">
        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Delivery Contact</p>
        <p className="text-sm text-foreground font-medium">{deceased.primaryContactName  || "—"}</p>
        <p className="text-sm text-muted-foreground">{deceased.primaryContactEmail || "—"}</p>
        <p className="text-sm text-muted-foreground">{deceased.primaryContactPhone || "—"}</p>
      </div>

      {/* Additional Notes */}
      <div className="space-y-2">
        <label htmlFor="additional-notes" className="text-sm font-medium text-foreground/70">
          Additional Notes for Our Team
        </label>
        <textarea
          id="additional-notes"
          name="additional_notes"
          value={additionalNotes}
          onChange={(e) => onNotesChange(e.target.value)}
          placeholder="Any special requests, specific music preferences, or important context about the photos..."
          rows={4}
          className="w-full px-4 py-3 rounded-lg border border-border bg-card text-foreground text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring transition resize-none"
        />
      </div>

      {/* Upload Progress */}
      {submitting && (
        <div className="space-y-2">
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">
              {uploadProgress < 100 ? "Uploading your memories..." : "Saving your order..."}
            </span>
            <span className="font-medium text-foreground">{uploadProgress}%</span>
          </div>
          <div className="progress-line">
            <div className="progress-line-fill" style={{ width: `${uploadProgress}%` }} />
          </div>
          <p className="text-xs text-muted-foreground">Please keep this page open until the upload completes.</p>
        </div>
      )}

      {/* CTA Button */}
      {!submitting && (
        <div className="space-y-3">
          <button
            type="button"
            onClick={onSubmit}
            className="w-full flex items-center justify-center gap-2 px-6 py-4 rounded-xl bg-primary text-primary-foreground font-semibold text-base hover:opacity-90 active:scale-[0.98] transition-all duration-150 shadow-sm"
          >
            <Send className="w-5 h-5" />
            Submit Order
          </button>
          <p className="text-xs text-center text-muted-foreground">
            Your memories are uploaded securely to our private vault. Our team will begin production within 24 hours.
          </p>
        </div>
      )}

      {submitting && (
        <div className="flex items-center justify-center gap-2 py-2 text-sm text-muted-foreground">
          <Loader2 className="w-4 h-4 animate-spin" />
          Processing...
        </div>
      )}

      {/* Fine print */}
      <p className="text-xs text-center text-muted-foreground">
        By submitting, you confirm you have the right to use all uploaded media. A preview will be
        delivered to {deceased.primaryContactEmail || "you"} within 24–48 hours.
      </p>
    </div>
  );
}

function SummaryCard({ icon, count, label }: { icon: React.ReactNode; count: number; label: string }) {
  return (
    <div className="p-4 rounded-xl border border-border bg-card flex items-center gap-3">
      <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
        {icon}
      </div>
      <div>
        <div className="font-['Playfair_Display'] font-700 text-2xl text-foreground leading-none">{count}</div>
        <div className="text-xs text-muted-foreground mt-0.5">{label}</div>
      </div>
    </div>
  );
}
